import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Modal,
  OutlinedInput,
  Stack,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PlacesAutocomplete from "react-places-autocomplete";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { boxModalStyle, suggestionBackgroundColor } from "../../BookingStyle";

const placeSearchOptions = {
  componentRestrictions: { country: "PE" },
};

const SortableSubRuteItem = ({ subRoute, index, total, onRemove }) => {
  const { ref, handleRef, isDragging, isDropTarget } = useSortable({
    id: subRoute.id,
    index,
    group: "booking-sub-rutes",
  });

  return (
    <Box
      ref={ref}
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: index === total - 1 ? "none" : "1px solid",
        borderColor: "divider",
        backgroundColor: isDropTarget ? "action.hover" : "background.paper",
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
          {index + 1}. {subRoute.name}
        </Typography>
        <DragHandleIcon
          ref={handleRef}
          sx={{ flexShrink: 0, cursor: "grab" }}
        />
        <IconButton
          size="small"
          color="error"
          aria-label="Eliminar subruta"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove(subRoute.id);
          }}
          sx={{ flexShrink: 0 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const SubRoutesModal = ({
  open,
  onClose,
  addressOrigin,
  subRutes,
  subRouteSearch,
  subRouteError,
  onSubRouteSearchChange,
  onSelectSubRoute,
  onAcceptSubRoute,
  onRemoveSubRoute,
  onDragOver,
  onDragEnd,
  destinationPlaceholder,
  onMapClick,
}) => (
  <Modal open={open} onClose={onClose} aria-labelledby="sub-routes-modal-title">
    <Box
      sx={{
        ...boxModalStyle,
        width: {
          xs: "92%",
          sm: "520px",
        },
      }}
    >
      <Typography
        id="sub-routes-modal-title"
        variant="h6"
        component="h2"
        sx={{ mb: 2, fontWeight: "bold" }}
      >
        MIS RUTAS
      </Typography>
      <Stack spacing={2}>
        <Box
          sx={{
            maxHeight: 220,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "4px",
            mb: 2,
            p: 1,
          }}
        >
          <p
            style={{
              fontSize: 14,
              marginLeft: "-3px",
              marginTop: "-5px",
              fontWeight: "bold",
            }}
          >
            {" "}
            Origen:
          </p>
          <Typography variant="body2">{addressOrigin}</Typography>
        </Box>
      </Stack>

      <Stack spacing={2}>
        <Box
          sx={{
            maxHeight: 220,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "4px",
          }}
        >
          <DragDropProvider onDragOver={onDragOver} onDragEnd={onDragEnd}>
            {subRutes.map((subRoute, index) => (
              <SortableSubRuteItem
                key={subRoute.id}
                subRoute={subRoute}
                index={index}
                total={subRutes.length}
                onRemove={onRemoveSubRoute}
              />
            ))}
          </DragDropProvider>
        </Box>

        <PlacesAutocomplete
          value={subRouteSearch}
          onChange={onSubRouteSearchChange}
          onSelect={onSelectSubRoute}
          searchOptions={placeSearchOptions}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading,
          }) => (
            <div key={suggestions.description}>
              <FormControl fullWidth variant="outlined" error={!!subRouteError}>
                <OutlinedInput
                  aria-describedby="sub-route-helper-text"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={onMapClick}
                        edge="end"
                        sx={{
                          color: "primary.main",
                          transition:
                            "background-color 0.2s ease, color 0.2s ease",
                          "&:hover": {
                            bgcolor: "primary.main",
                            color: "common.white",
                          },
                        }}
                      >
                        <LocationOnIcon fontSize="medium" />
                      </IconButton>
                    </InputAdornment>
                  }
                  {...getInputProps({
                    placeholder: destinationPlaceholder,
                    className: "location-search-input",
                  })}
                />
                {subRouteError && (
                  <FormHelperText id="sub-route-helper-text" role="alert">
                    {subRouteError}
                  </FormHelperText>
                )}
              </FormControl>
              <Box className="autocomplete-dropdown-container suggestionsDestination">
                {loading && <div>Loading...</div>}
                {suggestions.map((suggestion) => {
                  const className = suggestion.active
                    ? "suggestion-item--active"
                    : "suggestion-item";
                  const style = suggestion.active
                    ? suggestionBackgroundColor("#fafafa")
                    : suggestionBackgroundColor("#ffffff");
                  return (
                    <div
                      key={suggestion.description}
                      {...getSuggestionItemProps(suggestion, {
                        className,
                        style,
                      })}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  );
                })}
              </Box>
            </div>
          )}
        </PlacesAutocomplete>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button variant="contained" color="error" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onAcceptSubRoute}
          >
            Aceptar
          </Button>
        </Box>
      </Stack>
    </Box>
  </Modal>
);

export default SubRoutesModal;
